import { Component, Input, OnDestroy, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { ShareItResource, ShareItShareEntityType } from 'app/share-it/models/share-it';
import { combineLatest, Subscription } from 'rxjs';
import { CommonState } from 'search/full-card/models/card-header';
import { EditionData } from '../../../../citation/models/editions';
import { Edition, Entity, EntityTypes, FormatGroup } from '../../../../entity/models/entity';
import { getSelectedRollupTabIndex } from '../../../../entity/reducers/entity.reducer';
import { selectEntity } from '../../../../entity/reducers/full-entity.reducer';
import { ActivatedRoute } from '@angular/router';
import { DiscoverV2BffService } from 'app/elements-v2/service/discover-v2-bff.service';
import { Cover } from '../../../../models/cover';
import { BookCover } from 'search/models/search-results';
import { WindowRefService } from '../../../../services/window-ref.service';
import { ShareOptions } from '../../../../models/MetaData';
import { stripSpecialCharactersAndSymbols, truncateString } from 'core/utils/string-helper';

@Component({
  selector: 'app-format-group-toolbar-line',
  templateUrl: './format-group-toolbar-line.component.html',
  styleUrls: ['./format-group-toolbar-line.component.scss']
})
export class FormatGroupToolbarLineComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public fgOrEntity: FormatGroup | Entity;
  @Input() public text?: string;
  @Input() public materialType?: string;
  @Input() public images?: Cover | BookCover;

  public subscriptions = new Subscription();
  public resourceData: ShareItResource;
  public editionsData: EditionData[];
  public selectedTabRecordId: string;
  public selectedTabName: string;
  public queryParams: object;
  public frcShowShareIcon: boolean;
  public shareData: ShareOptions;

  constructor(
    private route: ActivatedRoute,
    public store: Store<CommonState>,
    private discoverV2BffService: DiscoverV2BffService,
    private readonly windowRefService: WindowRefService
  ) {
  }

  public ngOnInit() {
    this.subscriptions.add(combineLatest([
      this.store.pipe(select(getSelectedRollupTabIndex(this.fgOrEntity.id))),
      this.store.pipe(select(selectEntity))
    ]).subscribe(([selectedTabIndex, entity]) => {
      if (entity && this.isFormatGroup(this.fgOrEntity) && this.fgOrEntity.materialTabs?.length) {
        this.extractEditionsData(selectedTabIndex, entity);
      }
    }));
    this.resourceData = this.populateResourceData(this.fgOrEntity);
    this.queryParams = this.route.snapshot.queryParams;
    this.subscriptions.add(
      this.discoverV2BffService.$page.subscribe(page => {
        this.frcShowShareIcon = page.settings?.social_media_display?.frc || false;
      })
    );

    this.shareTheLink();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shareData) {
      this.shareTheLink();
    }
  }

  public ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }

  private extractEditionsData(selectedTabIndex: number, entity: Entity | FormatGroup) {
    if (this.isFormatGroup(entity) && this.isFormatGroup(this.fgOrEntity)) {
      this.editionsData = this.fgOrEntity.materialTabs?.[selectedTabIndex]?.editions?.map((edition: Edition) => ({
        instanceId: edition.id,
        editionStatement: [edition.edition],
        publicationDate: edition.publicationDate
      } as EditionData));
      this.selectedTabName = this.fgOrEntity.materialTabs?.[selectedTabIndex]?.name;
      this.selectedTabRecordId = this.fgOrEntity.materialTabs?.[selectedTabIndex]?.editions?.[0].recordId;
      this.resourceData = this.populateResourceData(this.fgOrEntity);
    }
  }

  private isFormatGroup(fgOrEntity: FormatGroup | Entity): fgOrEntity is FormatGroup {
    return fgOrEntity.entityType === EntityTypes.FORMAT_GROUP;
  }

  private getUrlForResourceData(fgOrEntity: FormatGroup | Entity): string {
    return this.isFormatGroup(fgOrEntity) ?
      `/search/card?recordId=${this.selectedTabRecordId}` :
      `/search/card?id=${fgOrEntity.id}&entityType=${fgOrEntity.entityType}`;
  }

  private populateResourceData(fgOrEntity: FormatGroup | Entity): ShareItResource {
    return {
      type: ShareItShareEntityType.RESOURCE,
      attributes: {
        id: fgOrEntity.id,
        url: this.getUrlForResourceData(fgOrEntity),
        title: this.isFormatGroup(fgOrEntity) ? fgOrEntity.title : '',
        author: this.isFormatGroup(fgOrEntity) ? (fgOrEntity.primaryAgent?.label || '') : '',
        publicationDate: this.isFormatGroup(fgOrEntity) ? (fgOrEntity.publicationDate || '') : '',
        resourceType: fgOrEntity.entityType
      }
    };
  }

  public shareTheLink() {
    const description = stripSpecialCharactersAndSymbols(this.text);
    const image = this.images?.coverUrl?.['large']
      || this.images?.coverUrl?.['medium']
      || this.images?.coverUrl?.['small'];
    const { title, author } = this.resourceData.attributes;

    this.shareData = {
      id: this.fgOrEntity.id,
      title: encodeURIComponent(truncateString(title, 50)),
      author: encodeURIComponent(truncateString(author, 50)),
      description: encodeURIComponent(truncateString(description, 150)),
      materialType: this?.materialType,
      image,
      url: encodeURIComponent(this.windowRefService.nativeWindow().location.href)
    };
  }
}
